// Индивидуальный букет L по промокоду: короткая анкета, дата не сегодня, адрес.
// Букет бесплатный, доставка оплачивается как обычно. Заявка уходит флористу, он связывается с клиентом.

import { useEffect, useState } from 'react';
import { api, type Slot } from '../lib/api.ts';
import { useApp } from '../lib/store.tsx';
import { haptic } from '../lib/tg.ts';
import { hh, rub, weekdayShort } from '../lib/format.ts';
import { ErrorNote, PhotoPicker, Spinner, TitleTop } from '../ui/kit.tsx';
import { AddressField, PhoneField, zoneOf, type Address } from './checkout.tsx';

const MOODS = ['Нежное', 'Яркое', 'Спокойное', 'Романтичное', 'Смелое'];

export function Individual() {
  const { go, me, config, reloadMe } = useApp();
  const grant = me?.promos.find((p) => p.kind === 'individual') ?? null;
  const [forWhom, setForWhom] = useState('');
  const [likes, setLikes] = useState('');
  const [dislikes, setDislikes] = useState('');
  const [mood, setMood] = useState<string | null>(null);
  const [ref, setRef] = useState<string | null>(null);
  const [days, setDays] = useState<{ tomorrow: string } | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [slotFrom, setSlotFrom] = useState<number | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [extra, setExtra] = useState('');
  const [rName, setRName] = useState('');
  const [rPhone, setRPhone] = useState('');
  const [phone, setPhone] = useState(me?.user.phone ?? '');
  const [agree, setAgree] = useState(Boolean(me?.user.consentPd));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.slots().then((r) => {
      setDays({ tomorrow: r.tomorrow });
      setDate(r.tomorrow);
    }, (e) => setError(e.message));
  }, []);
  useEffect(() => {
    if (!date) return;
    setSlots(null);
    api.slots(date).then((r) => {
      setSlots(r.slots);
      setSlotFrom(r.slots.find((s) => s.available)?.from ?? null);
    }, (e) => setError(e.message));
  }, [date]);

  if (!me) return <main className="screen"><Spinner /></main>;
  if (!grant)
    return (
      <main className="screen">
        <TitleTop title="Индивидуальный букет" />
        <p className="p">Этот подарок уже использован или срок закончился.</p>
      </main>
    );

  const zone = zoneOf(address);
  const delivery = zone === 1 ? config?.delivery.zone1 : zone === 2 ? config?.delivery.zone2 : zone === 3 ? config?.delivery.zone3 : null;
  const quote = address && (zone === 4 || zone === null);
  const missing =
    forWhom.trim().length < 2 ? 'Расскажи, для кого букет и какой повод' :
    !date || slotFrom === null ? 'Выбери день и время доставки' :
    !address ? 'Укажи адрес доставки' :
    phone.replace(/\D/g, '').length < 10 ? 'Нужен телефон для чека' :
    !agree ? 'Нужно согласие с условиями' : null;

  const submit = async () => {
    if (missing) return setError(missing);
    setBusy(true);
    setError(null);
    try {
      if (!me.user.consentPd) {
        await api.consent(me.user.consentAds);
        reloadMe();
      }
      const brief = [`Для кого и повод: ${forWhom.trim()}`, likes.trim() && `Любит: ${likes.trim()}`, dislikes.trim() && `Не надо: ${dislikes.trim()}`, mood && `Настроение: ${mood.toLowerCase()}`, ref && 'Референс: фото ниже']
        .filter(Boolean)
        .join('\n');
      const o = await api.createOrder({
        kind: 'individual',
        size: 'L',
        promoId: grant.id,
        brief,
        date,
        slotFrom,
        address: address!.value,
        addressExtra: extra || undefined,
        lat: address!.lat ?? undefined,
        lon: address!.lon ?? undefined,
        insideMkad: address!.insideMkad,
        handover: 'hand',
        recipientName: rName.trim() || undefined,
        recipientPhone: rPhone.trim() || undefined,
        phone,
      });
      if (ref) await api.support({ topic: 'order', text: 'Референс к индивидуальному букету', orderId: o.publicId, photo: ref, quiet: true }).catch(() => {});
      haptic.ok();
      reloadMe();
      if (o.status === 'draft') go({ name: 'order', orderId: o.publicId });
      else go({ name: 'pay', orderId: o.publicId });
    } catch (e: any) {
      haptic.err();
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="screen" style={{ gap: 16 }}>
      <TitleTop title="Индивидуальный букет" eyebrow={`${grant.title}${grant.until ? ` · действует ${grant.until}` : ''}`} />
      <p className="p" style={{ margin: 0 }}>
        Флорист соберёт авторский букет L специально под тебя. Расскажи, каким он должен быть, а флорист свяжется, уточнит детали и перед отправкой пришлёт фото.
      </p>

      <label className="field">
        <span>Для кого и какой повод</span>
        <input className="input" value={forWhom} maxLength={200} onChange={(e) => setForWhom(e.target.value)} placeholder="Например: маме на юбилей" />
      </label>
      <label className="field">
        <span>Любимые цветы и цвета</span>
        <input className="input" value={likes} maxLength={300} onChange={(e) => setLikes(e.target.value)} placeholder="Например: пионы, всё пудровое" />
      </label>
      <label className="field">
        <span>Чего точно не надо</span>
        <input className="input" value={dislikes} maxLength={300} onChange={(e) => setDislikes(e.target.value)} placeholder="Например: без лилий" />
      </label>
      <div className="stack s6">
        <span className="label">Настроение</span>
        <div className="wrap">
          {MOODS.map((m) => (
            <button key={m} type="button" className="chip sm" aria-pressed={mood === m} onClick={() => setMood(mood === m ? null : m)}>
              {m}
            </button>
          ))}
        </div>
      </div>
      <div className="stack s6">
        <span className="label">Фото для вдохновения, по желанию</span>
        <PhotoPicker value={ref} onChange={setRef} />
      </div>

      <div className="stack s6">
        <span className="label">Когда привезти</span>
        <span className="tiny">Индивидуальный букет собираем не день в день, поэтому ближайшая доставка завтра.</span>
        {days && (
          <label className="tile" style={{ position: 'relative', minHeight: 52, justifyContent: 'space-between', cursor: 'pointer' }}>
            <span>{date ? weekdayShort(date) : 'Выбрать день'}</span>
            <span className="tiny">изменить</span>
            <input type="date" aria-label="День доставки" min={days.tomorrow} value={date ?? ''} style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%' }} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </label>
        )}
        {!slots ? (
          <Spinner />
        ) : (
          <div className="grid3">
            {slots.map((s) => (
              <button key={s.from} type="button" className="chip" disabled={!s.available} aria-pressed={slotFrom === s.from} onClick={() => setSlotFrom(s.from)} style={{ minHeight: 48, borderRadius: 14, padding: 4, fontSize: 14, whiteSpace: 'nowrap', ...(s.available ? {} : { background: 'var(--sand)', borderStyle: 'dashed', color: 'var(--muted)' }) }}>
                {`${hh(s.from)}–${hh(s.to)}`}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="stack">
        <AddressField value={address} onChange={setAddress} />
        <input className="input" placeholder="Квартира, подъезд, домофон, этаж" value={extra} maxLength={200} onChange={(e) => setExtra(e.target.value)} aria-label="Подсказка курьеру" />
        <span className="tiny">{quote ? 'Это за МКАД. Флорист посчитает доставку в течение часа.' : delivery != null ? `Доставка ${rub(delivery)}, оплачивается при отправке заявки.` : 'Доставка оплачивается при отправке заявки, цена зависит от зоны.'}</span>
      </div>

      <div className="stack s6">
        <span className="label">Кому вручить, если не тебе</span>
        <input className="input" placeholder="Имя получателя" value={rName} maxLength={60} onChange={(e) => setRName(e.target.value)} />
        <input className="input" type="tel" inputMode="tel" placeholder="Телефон получателя" value={rPhone} onChange={(e) => setRPhone(e.target.value)} />
      </div>

      <div className="stack s6">
        <span className="label">Чек и связь по заказу</span>
        <PhoneField value={phone} onChange={setPhone} />
      </div>

      <label className="check">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        <span>
          Принимаю условия{' '}
          <button type="button" className="link" onClick={() => go({ name: 'docs' })}>
            оферты
          </button>{' '}
          и даю согласие на обработку данных
        </span>
      </label>

      <ErrorNote error={error} />
      <div className="bottom">
        <button type="button" className="btn" disabled={busy} onClick={submit}>
          {busy ? 'Отправляем…' : quote ? 'Отправить заявку' : `Отправить заявку${delivery != null ? ` · доставка ${rub(delivery)}` : ''}`}
        </button>
      </div>
    </main>
  );
}
